// Room Systems engineering engine — the deterministic "how much equipment
// does this room require?" layer between Room Criteria / Basis of Design
// (what performance the room needs) and everything downstream (pricing,
// electrical circuits, HVAC loads, and — later — generated canvas symbols).
//
//   Room Geometry + Room Criteria + Basis of Design
//                       |
//                       v
//             Engineering Calculation        (this module)
//                       |
//                       v
//              Calculated Requirement
//                       |
//                Optional Override           (a room's own TakeoffCriteria row)
//                       |
//                       v
//               Effective Value  ->  pricing / loads / circuits / geometry
//
// Architectural rule (per the 2026-09-11 engineering direction): the
// calculation determines what a room requires; drawn geometry represents
// that requirement and is never the source of the engineering quantity. A
// manual change is an explicit OVERRIDE recorded alongside the calculated
// value, never a silent replacement of it, so every quantity carries
// { calculatedQuantity, overrideQuantity, effectiveQuantity } and can explain
// itself ("why 12 HEPAs?").
//
// Pure functions, no DB access — same convention as takeoffMath.js,
// hvacLoadEngine.js and electricalCircuitEngine.js. Inputs:
//   room     - a TakeoffRooms row (LengthFt/WidthFt/HeightFt/ACH/PolygonJson,
//              plus the `_templateDefaults` attachment effectiveCriterion reads)
//   criteria - flat criterionKey -> value map (src/config/takeoffCriteria.js)
//   ctx      - { bodAttrs, settings, tunables }
//       bodAttrs  : buildBodOptionAttrs(sp_GetBasisOfDesignOptions rows) — the
//                   selected filter/fixture option's engineering attributes
//                   (DesignAirflowCfm, FilterFaceAreaSf, FixtureLumens, ...;
//                   db/62_basis_of_design_options.sql)
//       settings  : EstimateCostSettings row (VaPerLightingFixture fallback)
//       tunables  : TakeoffVariables rows merged over DEFAULT_SYSTEMS_TUNABLES
//                   (db/68_hepa_lighting_engine.sql — Basis of Design admin)
//
// Phase 1 scope (calculation foundation): HEPA count by airflow + ceiling
// coverage, lighting count by the lumen method + a spacing-criterion check.
// Persisted results (auditability) and generated canvas conditions are later
// phases — see CLAUDE.md "HEPA & Lighting Engineering Engine".

import { effectiveCriterion, criterionKey } from "../config/takeoffCriteria";
import { polygonOf, polygonArea } from "./takeoffGeometry";

// Bump when a formula below changes meaning, so a future persisted result
// row can say which version of the math produced it.
export const ROOM_SYSTEMS_CALC_VERSION = 1;

// Fallbacks for every admin-tunable design assumption this module reads.
// Each key is seeded as a TakeoffVariables row (db/68) and edited on Setup ▸
// Basis of Design; a caller that passes no tunables (or an unseeded key)
// resolves to these — never hardcode a new constant here without also
// seeding its row.
export const DEFAULT_SYSTEMS_TUNABLES = {
  HEPA_DEFAULT_DESIGN_CFM: 500,      // used ONLY when the selected filter option carries no DesignAirflowCfm
  HEPA_DEFAULT_MAX_CFM: 600,         // the most airflow one filter may carry; used ONLY when the selected filter option carries no MaxAirflowCfm
  HEPA_DEFAULT_FACE_AREA_SF: 8,      // 2x4 terminal filter; used only when the option carries no FilterFaceAreaSf
  LIGHTING_CU: 0.7,                  // coefficient of utilization (lumen method)
  LIGHTING_LLF: 0.8,                 // light loss factor (lumen method)
  LIGHTING_WORK_PLANE_HEIGHT_FT: 2.5,
  LIGHTING_DEFAULT_SPACING_CRITERION: 1.5, // used only when the fixture option carries no SpacingCriterion
};

// ---- helpers --------------------------------------------------------------------

function num(v, fallback = null) {
  if (v === null || v === undefined || v === "") return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function positive(v) {
  const n = num(v);
  return n !== null && n > 0 ? n : null;
}

// { "disc|fieldKey|OptionValue": row } — the selected option's engineering
// attributes. Includes INACTIVE rows on purpose: a room that picked an option
// an admin later retired must keep resolving that option's real airflow/lumen
// data, not silently drop to the defaults.
export function buildBodOptionAttrs(rows) {
  const map = {};
  for (const r of rows || []) map[`${r.Discipline}|${r.FieldKey}|${r.OptionValue}`] = r;
  return map;
}

// "60 fc" -> 60. The light_level criterion is a select whose option text
// carries the number; an admin-added option like "75 fc" parses the same way.
export function parseFootCandles(value) {
  const m = String(value ?? "").match(/([0-9]*\.?[0-9]+)/);
  return m ? +m[1] : null;
}

// A room's OWN TakeoffCriteria row (RoomID = this room) and nothing else —
// a template or project-level value is a default, never an override.
export function roomOverride(criteria, disc, fieldKey, room) {
  return num(criteria?.[criterionKey(disc, fieldKey, room.RoomID)]);
}

// Real traced footprint when the room has one (PolygonJson), else the
// bounding box — identical derivation to roomFormulaEngine/roomScopeFormulas.
export function roomGeometry(room) {
  const L = num(room.LengthFt, 0), W = num(room.WidthFt, 0), H = num(room.HeightFt, 0);
  const poly = polygonOf(room);
  const floorAreaSf = num(room.areaOverrideFt2) ?? (poly ? polygonArea(poly) : L * W);
  return { L, W, H, floorAreaSf, volumeCf: floorAreaSf * H };
}

function resolveTunables(ctx) {
  return { ...DEFAULT_SYSTEMS_TUNABLES, ...(ctx?.tunables || {}) };
}

// Room Build-Up gating (db/70): "presence is manual, quantity can be
// automatic". When the caller supplies ctx.roomElementsByRoom (RoomID ->
// RoomElements rows), a system only calculates for a room that carries an
// element for it — a HEPA-tagged Item/Assembly (AutoSystem or
// ElementQuantityBasis 'engine_hepa'), likewise for lighting. A caller that
// passes no element map (tests, legacy consumers) is ungated: everything
// calculates exactly as before elements existed.
export function systemEnabled(ctx, room, system) {
  const byRoom = ctx?.roomElementsByRoom;
  if (!byRoom) return true;
  const els = byRoom[room?.RoomID] || [];
  return els.some(e => e.AutoSystem === system || e.ElementQuantityBasis === `engine_${system}`);
}

// ---- HEPA -------------------------------------------------------------------------

// Required airflow   : RequiredCFM = FloorArea × Height × ACH / 60
// Airflow constraint : N_air = MAX( ROUND(RequiredCFM / DesignAirflowCfm), CEILING(RequiredCFM / MaxAirflowCfm) )
//                      i.e. the count nearest the TARGET airflow per filter, but never so few that a filter carries more
//                      than its MAXIMUM (rule "B", chosen by the product owner 2026-10-18: target 500, up to 600, fan
//                      modules 720 — the target and maximum are each filter option's own DesignAirflowCfm / MaxAirflowCfm;
//                      the tunables are the fallbacks). A maximum at or below the target adds nothing: CEILING(R / target).
// Coverage constraint: N_cov = CEILING(FloorArea × Coverage% / FilterFaceAreaSf)
// Calculated         : MAX(N_air, N_cov)
// Effective          : override ?? calculated
export function calcHepaSystem(room, criteria = {}, ctx = {}) {
  const tunables = resolveTunables(ctx);
  const geo = roomGeometry(room);
  const targetAch = num(room.ACH, 0);
  const requiredCfm = geo.volumeCf * targetAch / 60;

  // No HEPA element on this room -> airflow is still a room property, but
  // there is no filter count to size, generate or price.
  if (!systemEnabled(ctx, room, "hepa")) {
    return {
      enabled: false, filterType: "", option: null, requiredCfm,
      designAirflowPerUnit: null, designAirflowSource: null, minAirflowCfm: null, maxAirflowCfm: null, maxAirflowSource: null,
      faceAreaSf: null, faceAreaSource: null, coveragePercent: 0, requiredFaceAreaSf: 0,
      countByAirflow: 0, countByCoverage: 0, governingConstraint: null,
      calculatedQuantity: 0, overrideQuantity: null, effectiveQuantity: 0,
      actualAirflowPerUnit: 0, faceVelocityFpm: 0, actualCoveragePercent: 0, warnings: [],
    };
  }

  const filterType = effectiveCriterion(criteria, "mech", "filter_type", room).value || "";
  const option = ctx.bodAttrs?.[`mech|filter_type|${filterType}`] || null;

  const optionDesignCfm = positive(option?.DesignAirflowCfm);
  const designAirflowPerUnit = optionDesignCfm ?? tunables.HEPA_DEFAULT_DESIGN_CFM;
  const designAirflowSource = optionDesignCfm ? "option" : "default";
  const optionFaceArea = positive(option?.FilterFaceAreaSf);
  const faceAreaSf = optionFaceArea ?? tunables.HEPA_DEFAULT_FACE_AREA_SF;
  const minAirflowCfm = positive(option?.MinAirflowCfm);
  const optionMaxCfm = positive(option?.MaxAirflowCfm);
  const maxAirflowCfm = optionMaxCfm ?? positive(tunables.HEPA_DEFAULT_MAX_CFM);
  const maxAirflowSource = optionMaxCfm ? "option" : maxAirflowCfm ? "default" : null;

  const coveragePercent = Math.max(0, num(effectiveCriterion(criteria, "mech", "hepa_coverage_percent", room).value, 0));

  // snap: 3000.0000000001 / 500 must read as 6, not tip the count to 7 on floating-point noise
  const snap = (v) => +Number(v).toFixed(9);
  const maxUsable = maxAirflowCfm && maxAirflowCfm > designAirflowPerUnit ? maxAirflowCfm : null;
  const countByAirflow = requiredCfm > 0 && designAirflowPerUnit > 0
    ? (maxUsable ? Math.max(Math.round(snap(requiredCfm / designAirflowPerUnit)), Math.ceil(snap(requiredCfm / maxUsable))) : Math.ceil(snap(requiredCfm / designAirflowPerUnit)))
    : 0;
  const requiredFaceAreaSf = coveragePercent > 0 ? geo.floorAreaSf * coveragePercent / 100 : 0;
  const countByCoverage = requiredFaceAreaSf > 0 && faceAreaSf > 0 ? Math.ceil(requiredFaceAreaSf / faceAreaSf) : 0;

  const calculatedQuantity = Math.max(countByAirflow, countByCoverage);
  const governingConstraint = calculatedQuantity === 0 ? null : countByCoverage > countByAirflow ? "coverage" : "airflow";

  const overrideRaw = roomOverride(criteria, "mech", "hepa_count_override", room);
  const overrideQuantity = overrideRaw !== null && overrideRaw >= 0 ? Math.round(overrideRaw) : null;
  const effectiveQuantity = overrideQuantity ?? calculatedQuantity;

  const actualAirflowPerUnit = effectiveQuantity > 0 ? requiredCfm / effectiveQuantity : 0;
  const faceVelocityFpm = faceAreaSf > 0 ? actualAirflowPerUnit / faceAreaSf : 0;
  const actualCoveragePercent = geo.floorAreaSf > 0 ? (effectiveQuantity * faceAreaSf / geo.floorAreaSf) * 100 : 0;

  const warnings = [];
  if (maxAirflowCfm && actualAirflowPerUnit > maxAirflowCfm) {
    warnings.push({ code: "hepa_over_max_airflow", message: `HEPA airflow ${Math.round(actualAirflowPerUnit)} cfm/unit exceeds the selected filter's maximum of ${maxAirflowCfm} cfm.` });
  }
  if (minAirflowCfm && effectiveQuantity > 0 && actualAirflowPerUnit < minAirflowCfm) {
    warnings.push({ code: "hepa_under_min_airflow", message: `HEPA airflow ${Math.round(actualAirflowPerUnit)} cfm/unit is below the selected filter's minimum of ${minAirflowCfm} cfm.` });
  }
  if (overrideQuantity !== null && overrideQuantity !== calculatedQuantity) {
    warnings.push({ code: "hepa_override_differs", message: `HEPA count override (${overrideQuantity}) differs from the calculated requirement (${calculatedQuantity}).` });
  }
  if (requiredCfm > 0 && !option && filterType) {
    warnings.push({ code: "hepa_filter_option_unknown", message: `Terminal filtration "${filterType}" has no Basis of Design attributes — using default ${designAirflowPerUnit} cfm/unit.` });
  }

  return {
    enabled: true,
    filterType, option,
    requiredCfm,
    designAirflowPerUnit, designAirflowSource, minAirflowCfm, maxAirflowCfm, maxAirflowSource,
    faceAreaSf, faceAreaSource: optionFaceArea ? "option" : "default",
    coveragePercent, requiredFaceAreaSf,
    countByAirflow, countByCoverage, governingConstraint,
    calculatedQuantity, overrideQuantity, effectiveQuantity,
    actualAirflowPerUnit, faceVelocityFpm, actualCoveragePercent,
    warnings,
  };
}

// ---- Lighting -----------------------------------------------------------------------

// Lumen method   : RequiredLumens = FloorArea × TargetFC
//                  N_lum = CEILING(RequiredLumens / (FixtureLumens × CU × LLF))
// Spacing check  : MaxSpacing = SpacingCriterion × (CeilingHeight − WorkPlane)
//                  N_sp  = CEILING(L / MaxSpacing) × CEILING(W / MaxSpacing)   (bounding-box approximation)
// Calculated     : MAX(N_lum, N_sp) — only when the selected fixture carries FixtureLumens;
//                  with no fixture data there is no engineering basis and the
//                  legacy template/project lighting_fixture_count is used as a
//                  migration fallback instead.
// Effective      : override (the room's own lighting_fixture_count row) ?? calculated ?? fallback
export function calcLightingSystem(room, criteria = {}, ctx = {}) {
  const tunables = resolveTunables(ctx);
  const geo = roomGeometry(room);

  // No lighting element on this room -> no fixtures to count, load or price.
  if (!systemEnabled(ctx, room, "light")) {
    return {
      enabled: false, fixtureType: "", option: null, targetFc: null,
      fixtureLumens: null, coefficientOfUtilization: tunables.LIGHTING_CU, lightLossFactor: tunables.LIGHTING_LLF,
      requiredLumens: null, countByLumens: null, spacingCriterion: null, spacingSource: null,
      mountingHeightFt: 0, maxSpacingFt: 0, countBySpacing: null, governingConstraint: null,
      calculatedQuantity: null, overrideQuantity: null, fallbackQuantity: null, effectiveQuantity: 0, quantitySource: "disabled",
      wattsPerFixture: null, vaPerFixture: 0, vaSource: null, totalWatts: 0, totalVa: 0, warnings: [],
    };
  }

  const fixtureType = effectiveCriterion(criteria, "elec", "lighting", room).value || "";
  const option = ctx.bodAttrs?.[`elec|lighting|${fixtureType}`] || null;
  const targetFc = parseFootCandles(effectiveCriterion(criteria, "elec", "light_level", room).value);

  const fixtureLumens = positive(option?.FixtureLumens);
  const coefficientOfUtilization = tunables.LIGHTING_CU;
  const lightLossFactor = tunables.LIGHTING_LLF;

  const requiredLumens = targetFc && geo.floorAreaSf > 0 ? geo.floorAreaSf * targetFc : null;
  const lumensPerFixtureEffective = fixtureLumens ? fixtureLumens * coefficientOfUtilization * lightLossFactor : null;
  const countByLumens = requiredLumens !== null && lumensPerFixtureEffective > 0
    ? Math.ceil(requiredLumens / lumensPerFixtureEffective)
    : null;

  const optionSpacing = positive(option?.SpacingCriterion);
  const spacingCriterion = optionSpacing ?? tunables.LIGHTING_DEFAULT_SPACING_CRITERION;
  const mountingHeightFt = Math.max(0, geo.H - tunables.LIGHTING_WORK_PLANE_HEIGHT_FT);
  const maxSpacingFt = spacingCriterion * mountingHeightFt;
  const countBySpacing = countByLumens !== null && maxSpacingFt > 0 && geo.L > 0 && geo.W > 0
    ? Math.ceil(geo.L / maxSpacingFt) * Math.ceil(geo.W / maxSpacingFt)
    : null;

  const calculatedQuantity = countByLumens !== null ? Math.max(countByLumens, countBySpacing ?? 0) : null;
  const governingConstraint = calculatedQuantity === null ? null : (countBySpacing ?? 0) > countByLumens ? "spacing" : "lumens";

  const overrideRaw = roomOverride(criteria, "elec", "lighting_fixture_count", room);
  const overrideQuantity = overrideRaw !== null && overrideRaw >= 0 ? Math.round(overrideRaw) : null;
  const fallback = effectiveCriterion(criteria, "elec", "lighting_fixture_count", room);
  const fallbackQuantity = fallback.source && fallback.source !== "override" ? num(fallback.value, 0) : null;

  const effectiveQuantity = overrideQuantity ?? calculatedQuantity ?? fallbackQuantity ?? 0;
  const quantitySource = overrideQuantity !== null ? "override"
    : calculatedQuantity !== null ? "calculated"
    : fallbackQuantity !== null ? fallback.source
    : "none";

  // Electrical characteristics: the selected fixture's real numbers when the
  // option carries them; VaPerLightingFixture is the migration fallback only.
  const wattsPerFixture = positive(option?.FixtureWatts);
  const vaPerFixture = positive(option?.FixtureVa) ?? wattsPerFixture ?? positive(ctx.settings?.VaPerLightingFixture) ?? 100;
  const vaSource = positive(option?.FixtureVa) ? "option" : wattsPerFixture ? "option_watts" : positive(ctx.settings?.VaPerLightingFixture) ? "settings" : "default";
  const totalWatts = effectiveQuantity * (wattsPerFixture ?? vaPerFixture);
  const totalVa = effectiveQuantity * vaPerFixture;

  const warnings = [];
  if (overrideQuantity !== null && calculatedQuantity !== null && overrideQuantity !== calculatedQuantity) {
    warnings.push({ code: "lighting_override_differs", message: `Lighting fixture override (${overrideQuantity}) differs from the calculated requirement (${calculatedQuantity}).` });
  }
  if (calculatedQuantity === null && targetFc && fixtureType && !fixtureLumens) {
    warnings.push({ code: "lighting_fixture_no_lumens", message: `Lighting fixture "${fixtureType}" has no lumen rating in Basis of Design — fixture count falls back to the template/project default.` });
  }
  if (calculatedQuantity === null && !targetFc && geo.floorAreaSf > 0) {
    warnings.push({ code: "lighting_no_target_fc", message: "No light level (fc) selected — fixture count falls back to the template/project default." });
  }
  if (governingConstraint === "spacing") {
    warnings.push({ code: "lighting_spacing_governs", message: `Fixture spacing (max ${maxSpacingFt.toFixed(1)} ft at SC ${spacingCriterion}) requires more fixtures (${countBySpacing}) than the lumen method (${countByLumens}).` });
  }

  return {
    enabled: true,
    fixtureType, option, targetFc,
    fixtureLumens, coefficientOfUtilization, lightLossFactor,
    requiredLumens, countByLumens,
    spacingCriterion, spacingSource: optionSpacing ? "option" : "default",
    mountingHeightFt, maxSpacingFt, countBySpacing, governingConstraint,
    calculatedQuantity, overrideQuantity, fallbackQuantity, effectiveQuantity, quantitySource,
    wattsPerFixture, vaPerFixture, vaSource, totalWatts, totalVa,
    warnings,
  };
}

// ---- structured room result -----------------------------------------------------------

export function calcRoomSystems(room, criteria = {}, ctx = {}) {
  const geometry = roomGeometry(room);
  const hepa = calcHepaSystem(room, criteria, ctx);
  const lighting = calcLightingSystem(room, criteria, ctx);
  return {
    version: ROOM_SYSTEMS_CALC_VERSION,
    geometry,
    hvac: { targetAch: num(room.ACH, 0), requiredCfm: hepa.requiredCfm, hepa },
    electrical: { lighting },
    warnings: [...hepa.warnings, ...lighting.warnings],
  };
}

// The engineering outputs a pricing formula (EstimateCostFormulas.Expression,
// roomFormulaEngine.js) may reference — the formula engine CONSUMES these, it
// never re-derives them. Keep in sync with EstimateCostLibrary.jsx's Formulas
// tab help text.
export function roomSystemsFormulaVariables(systems) {
  const { geometry: g, hvac: { hepa }, electrical: { lighting } } = systems;
  return {
    roomVolume: g.volumeCf,
    supplyCfm: hepa.requiredCfm,
    hepaCount: hepa.effectiveQuantity,
    cfmPerHepa: hepa.actualAirflowPerUnit,
    hepaCoveragePercent: hepa.actualCoveragePercent,
    hepaFaceVelocity: hepa.faceVelocityFpm,
    lightingFixtureCount: lighting.effectiveQuantity,
    lightingRequiredLumens: lighting.requiredLumens ?? 0,
    lightingWatts: lighting.totalWatts,
    lightingVA: lighting.totalVa,
  };
}
